import axios from "axios";
import React, { useEffect, useState, useCallback } from "react";
import { env } from "../../config/env";
import {
  generateRandomInteger,
  generateUUID,
  hasSufficientBalance,
} from "../../constants/helper";
import { Box, Checkbox, Modal, Typography } from "@mui/material";
import { CoorporateType } from "../../constants/enum";
import MuiButton from "../basicComponent/MuiButton";
import PaymentInProgressBar from "../basicComponent/PaymentInProgressBar";
import { UtilService } from "../../services/shared/UtilService";
import AES from "../../aes/aes";
import { PGRes } from "../../constants/types";
import { useNavigate, useRoutes } from "react-router-dom";
import FlightLoader from "../basicComponent/FlightLoader";
import Swal from "sweetalert2";
import Notifier from "../basicComponent/Notifier";
import HotelLoader from "./hoteldetailcard/HotelLoader";
import { FlightServices } from "../../services/FlightServices/FlightServices";
import { useGlobalState } from "../../services/context/AuthContext";
import { useSweetAlert } from "../../services/customHooks/useSweetAlert";
import CheckoutForm from "../../model/jsCheckout/CheckoutForm";
import CryptoJS from "crypto-js";
import ReactDOM from "react-dom";

type PaymentModeCardProps = {
  grandTotal: number;
  createBooking: () => void;
  activeStep: number;
  bookingFor: "hotel" | "flight";
  couponCode: string;
  flightSearchDetail?: { BeginDate: string; Origin: any; Destination: any }[];
  passengerDetail?: [];
  isCheckBoxChecked?: boolean;
  termsAndCondition: React.ReactNode;
  hotelInfo?: {};
  travellerDetail?: {};
  saveFlightBookingDetails: (
    id: string,
    bookingType: string,
    paymentType: string,
    pgRes?: PGRes | null,
    bookingRes?: any | null
  ) => void;
};

export const PaymentModeCard = ({
  grandTotal,
  createBooking,
  activeStep,
  saveFlightBookingDetails,
  bookingFor,
  couponCode,
  flightSearchDetail,
  passengerDetail,
  termsAndCondition,
  isCheckBoxChecked,
  hotelInfo,
  travellerDetail,
}: PaymentModeCardProps) => {
  const { showInfo } = useSweetAlert();
  const { userInfo } = useGlobalState();
  const [showNotifier, setShowNotifier] = useState<boolean>(false);
  const [txnId, setTxnId] = useState<string>("");
  const prepaidBalance = userInfo?.prepaidBalance ?? 0;
  const postpaidBalance = userInfo?.postpaidBalance ?? 0;
  const hasPrepaidBalance = hasSufficientBalance(prepaidBalance, grandTotal);
  const hasPostpaidBalance = hasSufficientBalance(postpaidBalance, grandTotal);
  const label = { inputProps: { "aria-label": "Checkbox demo" } };
  const currency = userInfo?.currencyCode || "AED";
  const [open, setOpen] = useState(true);

  const [isPaymentGatwayChecked, setIsPaymentGatewayChecked] = useState(
    grandTotal > prepaidBalance
  );

  const [isPrepaidChecked, setIsPrepaidChecked] = useState<boolean>(false);
  const [isPostpaidChecked, setIsPostpaidChecked] = useState<boolean>(false);
  const [paymentStatus, setPaymentStatus] = useState(false);

  const isPaymentModeAvailable =
    isPaymentGatwayChecked || isPrepaidChecked || isPostpaidChecked;

  // ✅ Helper function to check if payment successful
  const isPaymentSuccessful = (status: string | undefined) => {
    if (!status) return false;
    return status.startsWith("Successful");
  };
  const isPaymentFailed = (status: string | undefined) => {
    if (!status) return false;
    return status.startsWith("Failed");
  };

  const handleFailPayment = async (id: string) => {
    const payload = {
      booking_txn_id: id,
    };
    try {
      const res = await UtilService.bookingFail(payload);
    } catch (error) {
      console.error("❌ Error in handleFailPayment:", error);
    }
  };
  const handleFailPaymentFromGateway = async (res: any) => {
    const payload = {
      ...res,
      sts: "failed",
      txn_id: res?.ag_ref,
      amount: grandTotal,
      method: "gateway",
    };
    try {
      const res = UtilService.paymentFailByPG(payload);
    } catch (error) {}
  };

  const handleCreateBooking = async (
    id: string,
    mode: string,
    parsedValue?: any
  ) => {
    try {
      const response = (await createBooking()) as any;
      const hotelRes = {
        booking_req: response?.booking_req,
        booking_res: response?.booking_res,
      };
      console.log("anjalo", response);

      const isBookingSuccessful =
        bookingFor === "flight"
          ? response?.data?.BookingStatus === 0 //TODO: change 0 to 1
          : !!response?.itineraryId;

      if (isBookingSuccessful && bookingFor === "flight") {
        const flightBookingRes = await getBooking(
          response?.data?.TransactionId
        );
        const finalFlightRes = {
          ...flightBookingRes,
          travellersDetail: travellerDetail,
        };
        const bookingRes = bookingFor === "flight" ? finalFlightRes : hotelRes;

        if (mode === "gateway") {
          const isDetailSavedSuccessfully = (await saveFlightBookingDetails(
            id,
            bookingFor,
            mode,
            parsedValue,
            bookingRes
          )) as any;

          setPaymentStatus(false);
        } else {
          const isDetailSavedSuccessfully = (await saveFlightBookingDetails(
            id,
            bookingFor,
            mode,
            null,
            bookingRes
          )) as any;
          setPaymentStatus(false);
        }
      } else {
        //FIXME:need to fix this section
        setPaymentStatus(false);
        if (mode === "gateway") {
          handleFailPaymentFromGateway({});
        } else {
          handleFailPayment(id);
        }
      }
    } catch (error) {
      //FIXME:need to fix this section
      setPaymentStatus(false);
      console.error("❌ Error while creating booking:", error);
      if (mode === "gateway") {
        handleFailPaymentFromGateway({});
      } else {
        handleFailPayment(id);
      }
    }
  };

  const getBooking = async (txnId: string) => {
    console.log("txnId", txnId);
    const id = txnId?.split("#");
    const payload = {
      transactionScreenId: id?.[1],
    };
    try {
      const response = await FlightServices.getBooking(payload);
      return response.data;
    } catch (error) {
      console.log("error while get booking", error);
    }
  };

  function encryptData(data: any, key: string) {
    const keyBytes = CryptoJS.enc.Base64.parse(key);
    const iv = CryptoJS.enc.Utf8.parse("0123456789abcdef");
    const textBytes = CryptoJS.enc.Utf8.parse(JSON.stringify(data));
    const encrypted = CryptoJS.AES.encrypt(textBytes, keyBytes, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.toString();
  }

  function generateHash(data: string) {
    return CryptoJS.SHA512(data).toString();
  }

  const handlePayFromCard = useCallback(() => {
    setPaymentStatus(false);
    const key = "lC7IcC3/wiqxs3wN0ksW0JLAxdnR8NhRm42k7LIofQk=";
    const meid = "202402150001";
    const amount = grandTotal;
    const planId = "";
    const uniqueId = "";

    const orderDetails = {
      txnDetails: {
        meId: meid,
        merchantOrderNo: Math.floor(Math.random() * (100000 - 1000)) + 1000,
        amount: amount,
        countryCode: "ARE",
        currencyCode: "AED",
        txnType: "SALE",
        channel: "WEB",
        userId: userInfo?.email,
        planId: planId,
        SuccessUrl: "/Response.jsp",
        FailUrl: "/Response.jsp",
      },
      customerDetails: {
        name: "jsCybsOn",
        email: userInfo?.email,
        phone: "500000001",
        uniqueId: uniqueId,
      },
      billingDetails: {
        bill_address: "",
        bill_city: "",
        bill_state: "",
        bill_country: "",
        bill_zip: "",
      },
      shipDetails: {
        ship_address: "",
        ship_city: "",
        ship_state: "",
        ship_country: "",
        ship_zip: "",
        ship_days: "",
        address_count: "",
      },
      itemDetails: {
        item_count: "",
        item_value: "",
        item_category: "",
      },
      otherDetails: {
        udf_1: "",
        udf_2: "",
        udf_3: "",
        udf_4: "",
        udf_5: "",
        udf_6: "",
      },
    };

    console.log("orderDetails:", orderDetails);

    const {
      meId,
      merchantOrderNo,
      amount: amt,
      countryCode,
      currencyCode,
    } = orderDetails.txnDetails;

    const compute = `${meId}|${merchantOrderNo}|${amt}|${countryCode}|${currencyCode}`;

    const encryptedData = encryptData(orderDetails, key);
    const hash = generateHash(compute);
    console.log("encryptedData", encryptedData);
    console.log("hash", hash);
    const jsCheckoutOptions = {
      merchantId: meId,
      internalKey: key,
      encryptedData: encryptedData,
      hash: hash,
      orderDetails: JSON.stringify(orderDetails),
      paymode: "",
    };

    const overlay = document.getElementById("overlay") as HTMLElement;
    const iframe = document.getElementById(
      "checkout-iframe"
    ) as HTMLIFrameElement;
    const loaderDiv = document.getElementById("loaderDiv") as HTMLElement;

    if (!overlay || !iframe) {
      console.error("Overlay or iframe element missing in DOM");
      return;
    }

    overlay.style.display = "flex";
    iframe.style.display = "block";

    const iframeSrc = `https://uatcheckout.safexpay.ae/ms-transaction-core-1-0/paymentRedirection/jsCheckoutLoader/${meId}`;
    iframe.src = iframeSrc;

    setTimeout(() => {
      iframe.contentWindow?.postMessage(jsCheckoutOptions, "*");
      setTimeout(() => {
        if (loaderDiv) loaderDiv.style.display = "none";
      }, 10000);
    }, 5000);
  }, []);

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.data.response) {
        console.log("Received response:", event.data.response);
        // setPaymentStatus(true);
        const paymentStatus = event?.data?.response?.[10];
        const transactionId = localStorage.getItem("txnId") ?? "";
        const gatewayResponse = {
          sts: event?.data?.response?.[10], //pending
          txn_id: event?.data?.response?.[1], //ag_ref
          amount: event?.data?.response?.[3], //amount
          method: "gateway",
          ag_id: "paygate",
          country: "ARE",
          currency: "AED",
          me_id: event?.data?.response?.[9], //meid
          order_number: event?.data?.response?.[2], //orderid
          status: event?.data?.response?.[10],
          transaction_date: event?.data?.response?.[6],
          transaction_time: event?.data?.response?.[7],
        };

        if (isPaymentSuccessful(paymentStatus)) {
          handleCreateBooking(transactionId, "gateway", gatewayResponse);
        } else {
          handleFailPaymentFromGateway(gatewayResponse);
          console.error("❌ Payment Not Successful:", paymentStatus);
          setPaymentStatus(false);
        }
      }
      if (event.data && event.data.type === "closeResponse") {
        const overlay = document.getElementById("overlay") as HTMLElement;
        if (overlay) overlay.style.display = "none";
      }
    };

    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, []);

  const holdPayment = async () => {
    const flightInfo = {
      searchDetail: flightSearchDetail,
      passengerDetail: passengerDetail,
    };
    const finalPayload = bookingFor === "flight" ? flightInfo : hotelInfo;
    const payload = {
      amount: grandTotal,
      method: isPaymentGatwayChecked
        ? "gateway"
        : isPrepaidChecked
        ? "prepaid"
        : "postpaid",
      booking_type: bookingFor,
      couponCode: couponCode,
      payload: finalPayload,
    };

    try {
      const res = await UtilService.paymentHold(payload);
      setPaymentStatus(true);
      if (isPaymentGatwayChecked && !!res) {
        localStorage.setItem("txnId", res?.booking_txn_id);
        setTxnId(res?.booking_txn_id);
        handlePayFromCard();
      } else {
        if (!!res) {
          setTxnId(res?.booking_txn_id);
          handleCreateBooking(
            res?.booking_txn_id,
            "topup",
            userInfo?.user_type?.toLocaleLowerCase() ?? ""
          );
        } else {
          setPaymentStatus(false);
        }
      }
    } catch (error) {
      console.error("❌ Error in holdPayment:", error);
    }
  };
  const handleBookNow = () => {
    if (bookingFor === "hotel" && isCheckBoxChecked && isPaymentModeAvailable) {
      setPaymentStatus(true);
      holdPayment();
    } else if (
      bookingFor === "flight" &&
      isCheckBoxChecked &&
      isPaymentModeAvailable
    ) {
      setPaymentStatus(true);
      holdPayment();
    } else if (!isCheckBoxChecked) {
      setShowNotifier(true);
    } else if (!isPaymentModeAvailable) {
      showInfo("", "Please select a payment mode");
    }
  };

  const handlePaymentGatewayCheckbox = () => {
    setIsPaymentGatewayChecked(true);
    setIsPrepaidChecked(false);
    setIsPostpaidChecked(false);
  };

  const handlePrepaidCheckBox = () => {
    setIsPaymentGatewayChecked(false);
    setIsPrepaidChecked(true);
    setIsPostpaidChecked(false);
  };

  const handlePostpaidCheckBox = () => {
    setIsPaymentGatewayChecked(false);
    setIsPrepaidChecked(false);
    setIsPostpaidChecked(true);
  };

  useEffect(() => {
    if (showNotifier) {
      const timer = setTimeout(() => setShowNotifier(false), 100);
      return () => clearTimeout(timer);
    }
  }, [showNotifier]);
  const loader = bookingFor === "flight" ? <FlightLoader /> : <HotelLoader />;

  return (
    <div>
      {paymentStatus && loader}

      <div className="card payment-card">
        <div className="card-header card-head-bg">
          <h5 className="payment-title">Payment Mode</h5>
        </div>

        <div className="px-2 pb-3">
          <PaymentOption
            label={"Prepaid Balance"}
            value={`${currency} ${prepaidBalance}`}
            checked={isPrepaidChecked}
            onChange={handlePrepaidCheckBox}
            disabled={!hasPrepaidBalance}
          />
          <PaymentOption
            label={"Postpaid Balance"}
            value={`${currency} ${postpaidBalance}`}
            checked={isPostpaidChecked}
            onChange={handlePostpaidCheckBox}
            disabled={!hasPostpaidBalance}
          />

          <PaymentOption
            label="Payment Gateway"
            value=""
            checked={isPaymentGatwayChecked}
            onChange={handlePaymentGatewayCheckbox}
            disabled={false}
          />
          {/* <MuiButton
            className={"main-btn flight-get-btn "}
            label="Book Now"
            type="submit"
            onClick={handlePayFromCard}
          /> */}
        </div>
      </div>
      <div>{termsAndCondition}</div>

      <div className="row">
        {activeStep === 2 && (
          <div className="user-button">
            <MuiButton
              className={"main-btn flight-get-btn "}
              label="Book Now"
              type="submit"
              onClick={handleBookNow}
            />
          </div>
        )}
      </div>
      {showNotifier && (
        <Notifier
          message="Please agree to the Terms & Conditions"
          variant="error"
        />
      )}

      <div
        id="overlay"
        style={{
          display: "none",
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 999999, // Always above everything
        }}
      >
        <div
          id="loaderDiv"
          style={{
            color: "#fff",
            zIndex: 1000000,
            position: "relative",
          }}
        >
          {bookingFor === "flight" ? <FlightLoader /> : <HotelLoader />}
        </div>
        <iframe
          id="checkout-iframe"
          title="checkout"
          style={{
            display: "none",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1000000,
            border: "none",
            background: "transparent",
          }}
        />
      </div>
    </div>
  );
};

// ✅ Small Reusable Payment Option Row
type PaymentOptionProps = {
  label: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  disabled: boolean;
};

const PaymentOption = ({
  label,
  value,
  checked,
  onChange,
  disabled,
}: PaymentOptionProps) => {
  return (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="form-check"
          style={{ marginLeft: "-24px", marginBottom: "-8px" }}
        >
          <Checkbox
            size="small"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
          />
          <label className="form-check-label">{label}</label>
        </div>
        {value && <div className="success-text pe-2">{value}</div>}
      </div>
    </>
  );
};
